import { z } from 'zod'

const UUIDSchema = z.string().uuid()
const UtcTimestampSchema = z.string().datetime({ offset: true })
const CalendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const HttpsImageUrlSchema = z.string().url().max(2048).refine((value) => value.startsWith('https://'))
const ExhibitionNameSchema = z.string().min(1).max(120).refine((name) => name === name.trim())
const ManufacturerTextSchema = z.string().min(1).max(120).refine((text) => text === text.trim())
const KopecksIntegerStringSchema = z.string().max(15).regex(/^(0|[1-9]\d*)$/)
const TotalKopecksIntegerStringSchema = z.string().max(17).regex(/^(0|[1-9]\d*)$/)

export const GoldExhibitionManufacturerInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('selected'), id: UUIDSchema }).strict(),
  z.object({ kind: z.literal('freeform'), text: ManufacturerTextSchema }).strict(),
  z.object({ kind: z.literal('unspecified') }).strict(),
])

export const GoldExhibitionManufacturerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('selected'), id: UUIDSchema, displayName: z.string() }).strict(),
  z.object({ kind: z.literal('freeform'), text: ManufacturerTextSchema }).strict(),
  z.object({ kind: z.literal('unspecified') }).strict(),
])

const UpdatedAuditFields = {
  updatedBySubject: z.string().min(1).nullable(),
  updatedAt: UtcTimestampSchema.nullable(),
}

function validateUpdatedAudit(
  audit: { updatedBySubject: string | null; updatedAt: string | null },
  context: z.RefinementCtx,
): void {
  if ((audit.updatedBySubject === null) !== (audit.updatedAt === null)) {
    context.addIssue({ code: 'custom', path: ['updatedAt'], message: 'Updated actor and time must be present together.' })
  }
}

const ExhibitionSummaryObjectSchema = z.object({
  id: UUIDSchema,
  name: ExhibitionNameSchema,
  rowCount: z.number().int().min(0),
  totalWeightMilliGrams: z.number().int().min(0).max(200_000_000_000),
  totalAmountKopecks: TotalKopecksIntegerStringSchema,
  budgetKopecks: TotalKopecksIntegerStringSchema.nullable(),
  startsOn: CalendarDateSchema.nullable(),
  endsOn: CalendarDateSchema.nullable(),
  imageUrl: HttpsImageUrlSchema.nullable(),
  supplierCount: z.number().int().min(0),
  createdBySubject: z.string().min(1),
  createdAt: UtcTimestampSchema,
  ...UpdatedAuditFields,
}).strict()

export const GoldExhibitionSummarySchema = ExhibitionSummaryObjectSchema.superRefine((exhibition, context) => {
  validateUpdatedAudit(exhibition, context)
  if (exhibition.startsOn !== null && exhibition.endsOn !== null && exhibition.startsOn > exhibition.endsOn) {
    context.addIssue({ code: 'custom', path: ['endsOn'], message: 'End date cannot precede start date.' })
  }
})

const GoldExhibitionPurchaseRowObjectSchema = z.object({
  id: UUIDSchema,
  manufacturer: GoldExhibitionManufacturerSchema,
  weightMilliGrams: z.number().int().min(0).max(1_000_000_000).nullable(),
  priceKopecksPerGram: z.number().int().min(0).max(100_000_000).nullable(),
  amountKopecks: KopecksIntegerStringSchema.nullable(),
  createdBySubject: z.string().min(1),
  createdAt: UtcTimestampSchema,
  ...UpdatedAuditFields,
}).strict()

export const GoldExhibitionPurchaseRowSchema = GoldExhibitionPurchaseRowObjectSchema.superRefine((row, context) => {
  validateUpdatedAudit(row, context)
  if (row.weightMilliGrams === null || row.priceKopecksPerGram === null) {
    if (row.amountKopecks !== null) {
      context.addIssue({ code: 'custom', path: ['amountKopecks'], message: 'Incomplete rows cannot have a calculated amount.' })
    }
    return
  }
  const expected = ((BigInt(row.weightMilliGrams) * BigInt(row.priceKopecksPerGram) + BigInt(500)) / BigInt(1_000)).toString()
  if (row.amountKopecks !== expected) {
    context.addIssue({ code: 'custom', path: ['amountKopecks'], message: 'Row amount must use half-up kopeck rounding.' })
  }
})

const GoldExhibitionPurchaseBatchObjectSchema = z.object({
  id: UUIDSchema,
  weightMilliGrams: z.number().int().min(1).max(1_000_000_000),
  priceKopecksPerGram: z.number().int().min(0).max(100_000_000).nullable(),
  amountKopecks: KopecksIntegerStringSchema.nullable(),
  createdBySubject: z.string().min(1),
  createdAt: UtcTimestampSchema,
  ...UpdatedAuditFields,
}).strict()

export const GoldExhibitionPurchaseBatchSchema = GoldExhibitionPurchaseBatchObjectSchema.superRefine((batch, context) => {
  validateUpdatedAudit(batch, context)
  const expected = batch.priceKopecksPerGram === null
    ? null
    : ((BigInt(batch.weightMilliGrams) * BigInt(batch.priceKopecksPerGram) + BigInt(500)) / BigInt(1_000)).toString()
  if (batch.amountKopecks !== expected) {
    context.addIssue({ code: 'custom', path: ['amountKopecks'], message: 'Batch amount must use its purchase-time rate.' })
  }
})

export const GoldExhibitionSupplierSchema = z.object({
  id: UUIDSchema,
  name: ExhibitionNameSchema,
  manufacturerId: UUIDSchema.nullable(),
  manufacturerDisplayName: z.string().nullable(),
  manufacturerAnalyticsKey: z.string().min(1).max(160).nullable(),
  priceKopecksPerGram: z.number().int().min(0).max(100_000_000).nullable(),
  totalWeightMilliGrams: z.number().int().min(0).max(200_000_000_000),
  totalAmountKopecks: TotalKopecksIntegerStringSchema,
  createdBySubject: z.string().min(1),
  createdAt: UtcTimestampSchema,
  ...UpdatedAuditFields,
  batches: z.array(GoldExhibitionPurchaseBatchSchema),
}).strict().superRefine((supplier, context) => {
  validateUpdatedAudit(supplier, context)
  if ((supplier.manufacturerId === null) !== (supplier.manufacturerAnalyticsKey === null)) {
    context.addIssue({ code: 'custom', path: ['manufacturerAnalyticsKey'], message: 'Manufacturer analytics key must match the linked manufacturer.' })
  }
})

const GoldExhibitionDetailObjectSchema = ExhibitionSummaryObjectSchema.extend({
  rows: z.array(GoldExhibitionPurchaseRowSchema),
  suppliers: z.array(GoldExhibitionSupplierSchema),
}).strict()

export const GoldExhibitionDetailSchema = GoldExhibitionDetailObjectSchema.superRefine((exhibition, context) => {
  validateUpdatedAudit(exhibition, context)
  if (exhibition.startsOn !== null && exhibition.endsOn !== null && exhibition.startsOn > exhibition.endsOn) {
    context.addIssue({ code: 'custom', path: ['endsOn'], message: 'End date cannot precede start date.' })
  }
  let totalWeightMilliGrams = BigInt(0)
  let totalAmountKopecks = BigInt(0)
  if (exhibition.suppliers.length > 0) {
    const batches = exhibition.suppliers.flatMap((supplier) => supplier.batches)
    if (exhibition.rowCount !== batches.length) {
      context.addIssue({ code: 'custom', path: ['rowCount'], message: 'Row count must match supplier purchase batches.' })
    }
    for (const batch of batches) {
      if (batch.amountKopecks !== null) {
        totalWeightMilliGrams += BigInt(batch.weightMilliGrams)
        totalAmountKopecks += BigInt(batch.amountKopecks)
      }
    }
  } else {
    if (exhibition.rowCount !== exhibition.rows.length) {
      context.addIssue({ code: 'custom', path: ['rowCount'], message: 'Row count must match the detail rows.' })
    }
    for (const row of exhibition.rows) {
      if (row.weightMilliGrams !== null && row.priceKopecksPerGram !== null && row.amountKopecks !== null) {
        totalWeightMilliGrams += BigInt(row.weightMilliGrams)
        totalAmountKopecks += BigInt(row.amountKopecks)
      }
    }
  }
  if (BigInt(exhibition.totalWeightMilliGrams) !== totalWeightMilliGrams) {
    context.addIssue({ code: 'custom', path: ['totalWeightMilliGrams'], message: 'Total weight must match complete rows.' })
  }
  if (BigInt(exhibition.totalAmountKopecks) !== totalAmountKopecks) {
    context.addIssue({ code: 'custom', path: ['totalAmountKopecks'], message: 'Total amount must sum rounded row amounts.' })
  }
})

export const GoldExhibitionListResponseSchema = z.object({
  items: z.array(GoldExhibitionSummarySchema).max(100),
  nextCursor: z.string().nullable(),
}).strict()

export const GoldExhibitionDetailResponseSchema = z.object({ item: GoldExhibitionDetailSchema }).strict()
export const GoldExhibitionPurchaseRowResponseSchema = z.object({ item: GoldExhibitionPurchaseRowSchema }).strict()

export const GoldExhibitionNameRequestSchema = z.object({
  name: ExhibitionNameSchema,
  budgetKopecks: z.number().int().min(0).max(100_000_000_000).nullable().optional(),
  startsOn: CalendarDateSchema.nullable().optional(),
  endsOn: CalendarDateSchema.nullable().optional(),
  imageUrl: HttpsImageUrlSchema.nullable().optional(),
}).strict().refine((value) => value.startsOn === undefined || value.endsOn === undefined || value.startsOn === null || value.endsOn === null || value.startsOn <= value.endsOn, { path: ['endsOn'] })

export const GoldExhibitionUpdateRequestSchema = z.object({
  name: ExhibitionNameSchema.optional(),
  budgetKopecks: z.number().int().min(0).max(100_000_000_000).nullable().optional(),
  startsOn: CalendarDateSchema.nullable().optional(),
  endsOn: CalendarDateSchema.nullable().optional(),
  imageUrl: HttpsImageUrlSchema.nullable().optional(),
}).strict().refine((value) => Object.values(value).some((item) => item !== undefined))

export const GoldExhibitionSupplierRequestSchema = z.object({
  name: ExhibitionNameSchema,
  manufacturerId: UUIDSchema.nullable(),
  priceKopecksPerGram: z.number().int().min(0).max(100_000_000).nullable(),
}).strict()

export const GoldExhibitionSupplierUpdateRequestSchema = z.object({
  priceKopecksPerGram: z.number().int().min(0).max(100_000_000),
}).strict()

export const GoldExhibitionPurchaseBatchRequestSchema = z.object({
  weightMilliGrams: z.number().int().min(1).max(1_000_000_000),
  priceKopecksPerGram: z.number().int().min(0).max(100_000_000).optional(),
}).strict()

export const GoldExhibitionSupplierResponseSchema = z.object({ item: GoldExhibitionSupplierSchema }).strict()
export const GoldExhibitionPurchaseBatchResponseSchema = z.object({ item: GoldExhibitionPurchaseBatchSchema }).strict()

export const GoldExhibitionArchivedRecordSchema = z.object({
  kind: z.enum(['exhibition', 'supplier']),
  id: UUIDSchema,
  name: ExhibitionNameSchema,
  exhibitionId: UUIDSchema,
  exhibitionName: ExhibitionNameSchema,
  archivedBySubject: z.string().min(1),
  archivedAt: UtcTimestampSchema,
}).strict()

export const GoldExhibitionArchiveResponseSchema = z.object({
  items: z.array(GoldExhibitionArchivedRecordSchema).max(100),
  nextCursor: z.string().nullable(),
}).strict()

export const GoldExhibitionPurchaseRowRequestSchema = z.object({
  manufacturer: GoldExhibitionManufacturerInputSchema,
  weightMilliGrams: z.number().int().min(0).max(1_000_000_000).nullable(),
  priceKopecksPerGram: z.number().int().min(0).max(100_000_000).nullable(),
}).strict()

export const GoldExhibitionListQuerySchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  cursor: z.string().optional(),
}).strict()

export type GoldExhibitionManufacturerInput = z.infer<typeof GoldExhibitionManufacturerInputSchema>
export type GoldExhibitionManufacturer = z.infer<typeof GoldExhibitionManufacturerSchema>
export type GoldExhibitionSummary = z.infer<typeof GoldExhibitionSummarySchema>
export type GoldExhibitionPurchaseRow = z.infer<typeof GoldExhibitionPurchaseRowSchema>
export type GoldExhibitionSupplier = z.infer<typeof GoldExhibitionSupplierSchema>
export type GoldExhibitionPurchaseBatch = z.infer<typeof GoldExhibitionPurchaseBatchSchema>
export type GoldExhibitionDetail = z.infer<typeof GoldExhibitionDetailSchema>
export type GoldExhibitionListResponse = z.infer<typeof GoldExhibitionListResponseSchema>
export type GoldExhibitionPurchaseRowRequest = z.infer<typeof GoldExhibitionPurchaseRowRequestSchema>
export type GoldExhibitionListQuery = z.infer<typeof GoldExhibitionListQuerySchema>
export type GoldExhibitionArchivedRecord = z.infer<typeof GoldExhibitionArchivedRecordSchema>
export type GoldExhibitionArchiveResponse = z.infer<typeof GoldExhibitionArchiveResponseSchema>
